import React, { useState, useEffect, useCallback, useRef } from 'react';
import { t } from '../i18n';
import { G2FaceMesh } from './glyphs';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface StageSubmittingProps {
  formData: { first_name: string; email: string; phone: string; concerns: string[]; };
  answers: Array<{ question_key: string; answer: string }>;
  selfiePhoto: File | null;
  /** Alignment transform resolved during the scan preview (undefined = center-crop). */
  photoTransform?: string;
  consentAt: string;
  clinicSlug: string;
  apiBase: string;
  onAdvance: () => void;
  onRetryForm: (formData: any) => void;
}

// Submit stage — the only place the widget touches the network. The heavy scan
// chamber already played in StageScanPreview, so this is a short "sending" beat:
// upload the selfie, POST the intake (atomic: contact + answers + selfie +
// consent), then advance to Success. The genuine report is generated server-side
// and delivered by email, exactly as before.
export const StageSubmitting = ({
  formData,
  answers,
  selfiePhoto,
  photoTransform,
  consentAt,
  clinicSlug,
  apiBase,
  onAdvance,
  onRetryForm,
}: StageSubmittingProps) => {
  const [isApiSuccess, setIsApiSuccess] = useState<boolean | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const reducedMotionRef = useRef(prefersReducedMotion());
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Object URL for the held selfie so it can sit inside the chamber ellipse with
  // the dot-mesh overlaid. Alignment reuses the transform resolved by the scan
  // preview (no second face-detection pass).
  useEffect(() => {
    if (!selfiePhoto) return;
    const url = URL.createObjectURL(selfiePhoto);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selfiePhoto]);

  const submitIntake = useCallback(async () => {
    // Keep the loader on screen for at least 3.5 s (or longer if the server is
    // still responding); the chamber animation already ran in the preview step.
    const minDisplayMs = 3500;
    const minDisplayTime = new Promise<void>(resolve => setTimeout(resolve, minDisplayMs));

    try {
      let selfieMetadata: {
        object_key: string;
        file_url: string;
        content_type: string;
        file_size: number;
      } | undefined;

      if (selfiePhoto) {
        const controller1 = new AbortController();
        const timeout1 = setTimeout(() => controller1.abort(), 10_000);
        let presignRes: Response;
        try {
          presignRes = await fetch(`${apiBase}/v1/public/uploads/selfie/presign`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              clinic_slug: clinicSlug,
              content_type: selfiePhoto.type,
              file_name: selfiePhoto.name,
              file_size: selfiePhoto.size,
            }),
            signal: controller1.signal,
          });
        } finally {
          clearTimeout(timeout1);
        }

        if (!presignRes!.ok) {
          const errBody = await presignRes!.json().catch(() => ({}));
          const err = new Error((errBody as any)?.error?.message ?? 'Selfie presign failed') as any;
          err.status = presignRes!.status;
          throw err;
        }

        const presign = await presignRes!.json();
        const uploadHeaders: Record<string, string> = { 'content-type': selfiePhoto.type };
        if (presign.headers) {
          Object.assign(uploadHeaders, presign.headers);
        }

        const uploadRes = await fetch(presign.upload_url, {
          method: 'PUT',
          headers: uploadHeaders,
          body: selfiePhoto,
        });

        if (!uploadRes.ok) {
          const err = new Error('Selfie upload failed') as any;
          err.status = uploadRes.status;
          throw err;
        }

        const uploadBody = await uploadRes.json().catch(() => ({}));
        selfieMetadata = {
          object_key: uploadBody.object_key ?? presign.object_key,
          file_url: uploadBody.file_url ?? presign.file_url,
          content_type: selfiePhoto.type,
          file_size: selfiePhoto.size,
        };
      }

      const payload: Record<string, unknown> = {
        clinic_slug: clinicSlug,
        first_name: formData.first_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        consent_given: true,
        consent_at: consentAt,
        ...(answers.length > 0 ? { answers } : {}),
        ...(selfieMetadata ? { selfie: selfieMetadata } : {}),
      };

      const controller2 = new AbortController();
      const timeout2 = setTimeout(() => controller2.abort(), 30_000);
      let intakeRes: Response;
      try {
        intakeRes = await fetch(`${apiBase}/v1/public/intake`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller2.signal,
        });
      } finally {
        clearTimeout(timeout2);
      }

      if (!intakeRes!.ok) {
        const errBody = await intakeRes!.json().catch(() => ({}));
        const err = new Error((errBody as any)?.error?.message ?? 'Intake submission failed') as any;
        err.status = intakeRes!.status;
        err.code = (errBody as any)?.error?.code;
        throw err;
      }

      await minDisplayTime;
      setIsApiSuccess(true);
      onAdvance();
    } catch (error: any) {
      await minDisplayTime;
      const status: number = error?.status ?? 0;
      const isAbort = error?.name === 'AbortError';
      const isNetwork = error instanceof TypeError || isAbort;

      if (status === 429 && error?.code === 'DEMO_DAILY_CAP_REACHED') {
        // Friendly demo-cap message — retrying today won't help, so no retry CTA.
        setApiError('cap');
      } else if (isNetwork || status === 0 || status >= 500) {
        setApiError('network');
      } else {
        setApiError('4xx');
      }
      setIsApiSuccess(false);
    }
  }, [apiBase, clinicSlug, formData, answers, selfiePhoto, consentAt, onAdvance]);

  useEffect(() => {
    void submitIntake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setIsApiSuccess(null);
    setApiError(null);
    void submitIntake();
  };

  const reduced = reducedMotionRef.current;
  const photoStyle: React.CSSProperties | undefined = photoTransform
    ? { transform: photoTransform, transformOrigin: '0 0' }
    : undefined;

  return (
    <div className="vc-frame stage-04-processing">
      <style>{`
        .stage-04-processing .vc-error-zone {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--sp-6);
          padding: 0 var(--sp-6) var(--sp-8);
        }
        .stage-04-processing .vc-error-message {
          font-size: var(--text-base);
          line-height: var(--leading-normal);
          text-align: center;
          color: var(--fg-on-chamber-secondary);
        }
        .stage-04-processing .vc-error-zone .vc-btn {
          align-self: stretch;
        }
      `}</style>

      <div className="vc-head">
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
        <span className="vc-steplabel">{t('submit.step')}</span>
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
      </div>

      {isApiSuccess === null && (
        <>
          <div className="vc-stage" aria-hidden="true">
            {selfiePhoto && photoUrl && (
              <div className="vc-photo-stack" aria-hidden="true">
                <div className="vc-photo-frame">
                  <img className="vc-photo" src={photoUrl} alt="" style={photoStyle} />
                </div>
                <div className="vc-photo-tint-lo"></div>
                <div className="vc-photo-tint-hi"></div>
              </div>
            )}
            <div className="vc-halo"></div>
            <div className="vc-depth"><i></i><i></i><i></i></div>
            <G2FaceMesh
              className="mesh"
              stroke="var(--mesh-stroke)"
              dot="var(--mesh-dot)"
              strokeWidth={0.75}
              dotR={1.7}
              meshOpacity={0.55}
              dotOpacity={1}
            />
            {!reduced && <div className="vc-scanpass vc-scanpass--sweep"></div>}
          </div>

          <h2 className="vc-title" role="heading" aria-level={2}>
            {t('submit.title.pre')}<em>{t('submit.title.em')}</em>
          </h2>
          <p className="vc-narration" aria-live="polite" aria-atomic="true">
            {t('submit.narration')}
          </p>

          <p className="vc-chamber-foot">
            {t('submit.foot')}
          </p>
        </>
      )}

      {isApiSuccess === false && apiError === '4xx' && (
        <div id="processing-error" tabIndex={-1} aria-live="assertive" role="alert" className="vc-error-zone">
          <p className="vc-error-message">
            {t('submit.error.4xx')}
          </p>
          <button className="vc-btn vc-btn--outline" onClick={() => onRetryForm(formData)}>
            {t('submit.error.review')}
          </button>
        </div>
      )}

      {isApiSuccess === false && apiError === 'network' && (
        <div id="processing-error" tabIndex={-1} aria-live="assertive" role="alert" className="vc-error-zone">
          <p className="vc-error-message">
            {t('submit.error.network')}
          </p>
          <button className="vc-btn vc-btn--primary" onClick={handleRetry}>
            {t('submit.error.tryAgain')}
          </button>
        </div>
      )}

      {isApiSuccess === false && apiError === 'cap' && (
        <div id="processing-error" tabIndex={-1} aria-live="assertive" role="alert" className="vc-error-zone">
          <p className="vc-error-message">
            {t('submit.error.cap')}
          </p>
        </div>
      )}

      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {isApiSuccess === null && t('submit.live')}
        {isApiSuccess === false && apiError === '4xx' && t('submit.error.4xx')}
        {isApiSuccess === false && apiError === 'network' && t('submit.error.network')}
        {isApiSuccess === false && apiError === 'cap' && t('submit.error.cap')}
      </div>
    </div>
  );
};
